import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal, ComponentType } from '@angular/cdk/portal';
import { ComponentRef, Injectable, OnDestroy } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable, Subject } from 'rxjs';
import { filter } from 'rxjs/operators';
import { ToastBaseComponent, ToastDataBaseComponent } from '../shared/components/toast/toast-base.component';
import { ToastHostComponent } from '../shared/components/toast/toast-host/toast-host.component';
import { AppSelectors } from '../state/app';
import { NotificationSettingsDto } from './api.services';
import { logger } from '../pbir/utils/logger';

const BC_TOAST_DISMISSAL = "insights_toast_dismissal";

const LOGGER = logger('ToastService');

@Injectable({
  providedIn: 'root'
})
export class ToastService implements OnDestroy{
  constructor(
    private overlayService: Overlay,
    private store: Store
  ) {
    this.overlay = this.overlayService.create({panelClass: "app-toast-host-overlay"});
    this.toastHost = this.overlay.attach(new ComponentPortal(ToastHostComponent));
    this.toastHost.instance.toastService = this;

    this.notifDissmisalBroadcastChannel = new BroadcastChannel(BC_TOAST_DISMISSAL);
    this.notifDissmisalBroadcastChannel.onmessage = event => this.dismissByBrodcast(event);

    this.store.select(AppSelectors.getSettings)
      .pipe(filter(s=>!!s.notificationSettings))
      .subscribe(s => this.applySettings(s.notificationSettings));
  }

  ngOnDestroy(): void {
    this.notifDissmisalBroadcastChannel.close();
  }

  notificationSettings: NotificationSettingsDto;
  overlay: OverlayRef;
  toastHost: ComponentRef<ToastHostComponent>;

  notifDissmisalBroadcastChannel: BroadcastChannel;

  toasts: ToastRef[] = [];


  applySettings(settings: NotificationSettingsDto) {
    this.notificationSettings = settings;
    this.toastHost.instance.setOrientation(this.notificationSettings.toastViewPortLocation)
  }

  overrideOrientation(location: number) {
    this.toastHost.instance.setOrientation(location)
  }

  restOrientationOverride() {
    this.toastHost.instance.setOrientation(this.notificationSettings.toastViewPortLocation)
  }

  open<T extends ToastBaseComponent<D>, D extends ToastDataBaseComponent>(toast: AppToast<T, D>): ToastCreationInfo {
    let index = this.toastHost.instance.addToast(toast);

    let dismissalSubject = new Subject<ToastDismissalInfo>();

    this.toasts.push({hostIndex: index, dismissEvent: dismissalSubject, id: toast.id })


    if (!!toast.dismissAfter)
      setTimeout(() => this.dismiss(index, true), toast.dismissAfter)

    return {dismissalSubject: dismissalSubject.asObservable(), index};
  }

  dismiss(index: number, autoDismissed: boolean, dismissedByBroadcast: boolean = false) {
    let toast = this.toasts.find(t => t.hostIndex == index);

    if (!toast)
      return;

    let listIndex = this.toasts.indexOf(toast);
    this.toasts.splice(listIndex,1);
    this.toastHost.instance.removeToast(index);

    if(!dismissedByBroadcast &&  !autoDismissed && !!toast.id)
      this.notifDissmisalBroadcastChannel.postMessage(<ToastDismissalBrodcastInfo>{id: toast.id, autoDismissed: false});

    toast.dismissEvent.next({ autoDismissed, dismissedByBroadcast });
  }

  dismissByBrodcast(event: MessageEvent<ToastDismissalBrodcastInfo>) {
    let toastRef = this.toasts.find(t => t.id == event.data.id);

    if (toastRef) {
      this.dismiss(toastRef.hostIndex, event.data.autoDismissed, true)
      return;
    }

    LOGGER.warn("Tried to Dismiss toast via broadcastChannel but toast was not found")
  }
}

export interface AppToast<T extends ToastBaseComponent<D>, D extends ToastDataBaseComponent> {
  component: ComponentType<T>
  data: D,
  dismissAfter?: number,
  id?: number
}

interface ToastRef{
  hostIndex: number,
  dismissEvent: Subject<ToastDismissalInfo>,
  id?: number
}

interface ToastDismissalBrodcastInfo{
  autoDismissed: boolean,
  id: number
}

export interface ToastDismissalInfo {
  autoDismissed: boolean,
  dismissedByBroadcast: boolean
}

export interface ToastCreationInfo {
  dismissalSubject: Observable<ToastDismissalInfo>,
  index: number
}
